import { useCallback, useEffect, useMemo } from 'react';
import { AlertCircle, Download, ExternalLink, FileText, Loader2, ScanText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DocumentViewerService } from '@/services/documentViewerService';
import { usePreviewPanelStore, type DocumentPreviewOcrSource, type DocumentPreviewTab } from '@/stores/previewPanelStore';
import { PreviewIconButton } from './previewPanelChrome';
import { useDocumentPreviewOcr } from './useDocumentPreviewOcr';

const OCR_SOURCE_BADGE: Record<Exclude<DocumentPreviewOcrSource, null>, { label: string; className: string }> = {
  ocr: { label: 'OCR', className: 'bg-grey-100 text-grey-600' },
  // Vision reconstructs content and is more interpretive than literal OCR.
  vision: { label: 'IA vision', className: 'bg-gold/10 text-gold' },
  native: { label: 'Texte natif', className: 'bg-grey-100 text-grey-600' },
};

/** Removes OCR image placeholders whose relative targets cannot resolve in the preview. */
function cleanOcrMarkdown(raw: string): string {
  return raw
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Sortir la pièce du panneau : nouvel onglet navigateur si la ressource est déjà
 * là, téléchargement sinon. Partagé par la chrome et par les états d'erreur du
 * corps, qui reproposent les deux mêmes issues.
 */
function useDocumentFileActions(tab: DocumentPreviewTab) {
  const handleDownload = useCallback(async () => {
    if (!tab.filePath) {
      // Un aperçu FABRIQUÉ (papier à en-tête converti en PDF) n'a pas de chemin stocké : ses octets
      // sont déjà là, sous une URL locale. On les rend sous le nom de l'onglet, et non sous
      // l'identifiant de l'URL que le lecteur PDF du navigateur aurait donné.
      if (tab.resource?.kind === 'object-url') {
        const lien = document.createElement('a');
        lien.href = tab.resource.url;
        lien.download = tab.fileName;
        lien.click();
      }
      return;
    }
    await DocumentViewerService.downloadDocument(tab.filePath, tab.fileName, tab.caseId, tab.documentId);
  }, [tab]);

  const handleOpenInNewTab = useCallback(async () => {
    if (tab.resource?.url) {
      window.open(tab.resource.url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (tab.filePath) {
      await DocumentViewerService.downloadDocument(tab.filePath, tab.fileName, tab.caseId, tab.documentId);
    }
  }, [tab]);

  return { handleDownload, handleOpenInNewTab };
}

/**
 * Bascule de VUE d'une pièce — ce qui change la façon de lire le document ouvre
 * la rangée à gauche, tandis que ce qui agit SUR le fichier reste à droite.
 */
export function DocumentPreviewTabViewToggle({ tab }: { tab: DocumentPreviewTab }) {
  const setViewMode = usePreviewPanelStore((state) => state.setViewMode);

  return (
    <>
      {tab.documentId && (
        <div className="flex flex-shrink-0 items-center overflow-hidden rounded-md border border-grey-200">
          <button
            type="button"
            onClick={() => setViewMode(tab.key, 'original')}
            className={cn(
              'h-7 px-2 text-xs font-medium transition-colors',
              tab.viewMode === 'original' ? 'bg-charcoal text-white' : 'text-grey-600 hover:bg-grey-100',
            )}
          >
            Original
          </button>
          <button
            type="button"
            onClick={() => setViewMode(tab.key, 'text')}
            className={cn(
              'h-7 px-2 text-xs font-medium transition-colors',
              tab.viewMode === 'text' ? 'bg-charcoal text-white' : 'text-grey-600 hover:bg-grey-100',
            )}
          >
            Texte extrait
          </button>
        </div>
      )}
    </>
  );
}

/** Ce qui fait sortir la pièce du panneau — rendu dans le groupe de droite. */
export function DocumentPreviewTabFileActions({ tab }: { tab: DocumentPreviewTab }) {
  const { handleDownload, handleOpenInNewTab } = useDocumentFileActions(tab);

  return (
    <>
      {tab.filePath && (
        <PreviewIconButton
          label={`Ouvrir ${tab.fileName} dans un nouvel onglet`}
          onClick={() => void handleOpenInNewTab()}
        >
          <ExternalLink className="h-4 w-4" aria-hidden="true" />
        </PreviewIconButton>
      )}
      {tab.resource && (
        <PreviewIconButton label={`Télécharger ${tab.fileName}`} onClick={() => void handleDownload()}>
          <Download className="h-4 w-4" aria-hidden="true" />
        </PreviewIconButton>
      )}
    </>
  );
}

/**
 * Corps d'un onglet pièce.
 *
 * WHY ce composant existe : `useDocumentPreviewOcr` et l'effet de chargement du
 * texte vivaient au sommet du panneau, donc s'exécutaient quel que soit l'onglet
 * actif. Un hook ne pouvant pas être appelé conditionnellement, les descendre ici
 * est ce qui garantit qu'aucune requête OCR ne peut partir sur un onglet qui
 * n'est pas une pièce : React démonte ce composant dès qu'on bascule ailleurs.
 * Les gardes du store (`getDocumentTab`) sont la seconde ligne de défense.
 */
export function DocumentPreviewTabBody({ tab }: { tab: DocumentPreviewTab }) {
  const { loadText, extractText } = useDocumentPreviewOcr(tab.key);
  const { handleDownload, handleOpenInNewTab } = useDocumentFileActions(tab);

  useEffect(() => {
    if (tab.viewMode === 'text' && tab.ocr.status === 'idle') {
      void loadText();
    }
  }, [tab.ocr.status, tab.viewMode, loadText]);

  const cleanedOcrText = useMemo(
    () => (tab.ocr.content ? cleanOcrMarkdown(tab.ocr.content) : ''),
    [tab.ocr.content],
  );

  const fileType = tab.fileType;
  const isLoading = tab.status === 'loading';
  const isConvertingToPreview = ['docx', 'doc', 'eml', 'msg'].includes(fileType) && isLoading;

  if (tab.viewMode === 'text') {
    return (
      <div className="h-full w-full overflow-auto bg-white">
        {tab.ocr.status === 'loading' && (
          <div className="flex h-full flex-col items-center justify-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-grey-400" />
            <p className="text-sm text-grey-500">Chargement du texte extrait…</p>
          </div>
        )}
        {tab.ocr.status === 'ready' && cleanedOcrText && (
          <div>
            {tab.ocr.source && (
              <div className="sticky top-0 flex items-center gap-2 border-b border-grey-100 bg-white/95 px-5 py-2 backdrop-blur">
                <span
                  className={cn(
                    'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                    OCR_SOURCE_BADGE[tab.ocr.source].className,
                  )}
                >
                  {OCR_SOURCE_BADGE[tab.ocr.source].label}
                </span>
                <span className="text-[11px] text-grey-400">Texte extrait, à vérifier avant tout usage</span>
              </div>
            )}
            <pre className="m-0 whitespace-pre-wrap break-words px-5 py-4 font-serif text-sm leading-relaxed text-charcoal">
              {cleanedOcrText}
            </pre>
          </div>
        )}
        {tab.ocr.status === 'extracting' && (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-grey-400" />
            <p className="text-sm text-grey-600">Extraction du texte en cours…</p>
            <p className="text-xs text-grey-400">Cela peut prendre quelques minutes pour un document scanné.</p>
          </div>
        )}
        {(tab.ocr.status === 'ready' && !cleanedOcrText) || tab.ocr.status === 'error' ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
            {tab.ocr.status === 'error' ? (
              <AlertCircle className="h-10 w-10 text-red-400" />
            ) : (
              <FileText className="h-10 w-10 text-grey-300" />
            )}
            <p className="text-sm font-medium text-grey-700">
              {tab.ocr.error ?? 'Aucun texte extrait disponible'}
            </p>
            <p className="max-w-xs text-xs text-grey-500">
              Le texte est extrait lors de la synthèse de la pièce. Les pièces plus anciennes peuvent nécessiter une extraction à la demande.
            </p>
            <Button variant="outline" size="sm" onClick={() => void extractText()} className="mt-1">
              <ScanText className="mr-2 h-4 w-4" />
              Extraire le texte maintenant
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <>
      {isLoading && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white">
          <Loader2 className="h-8 w-8 animate-spin text-grey-400" />
          <p className="text-sm text-grey-500">
            {isConvertingToPreview ? 'Préparation de la prévisualisation…' : 'Chargement du document...'}
          </p>
        </div>
      )}

      {tab.error && !isLoading && (
        <div className="flex h-full flex-col items-center justify-center gap-3 px-6">
          <AlertCircle className="h-8 w-8 text-red-400" />
          <p className="text-center text-sm text-grey-700">{tab.error}</p>
          <div className="flex w-full max-w-xs flex-col gap-2">
            {tab.filePath && (
              <Button variant="outline" size="sm" onClick={() => void handleOpenInNewTab()} className="w-full">
                <ExternalLink className="mr-2 h-4 w-4" />
                Ouvrir dans un nouvel onglet
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => void handleDownload()} className="w-full">
              <Download className="mr-2 h-4 w-4" />
              Télécharger
            </Button>
          </div>
        </div>
      )}

      {!tab.resource && !isLoading && !tab.error && (
        <div className="flex h-full flex-col items-center justify-center gap-4 px-6">
          <FileText className="h-10 w-10 text-grey-300" />
          <div className="text-center">
            <p className="text-sm font-medium text-grey-700">Aperçu non disponible</p>
            <p className="mt-1 text-xs text-grey-500">Ce type de fichier ne peut pas être prévisualisé dans le navigateur.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => void handleDownload()}>
            <Download className="mr-2 h-4 w-4" />
            Télécharger le fichier
          </Button>
        </div>
      )}

      {tab.resource && !isLoading && !tab.error && fileType === 'image' && (
        <div className="flex h-full w-full items-center justify-center overflow-auto bg-grey-100 p-4">
          <img
            src={tab.resource.url}
            alt={tab.fileName}
            className="max-h-full max-w-full rounded object-contain shadow-sm"
          />
        </div>
      )}

      {tab.resource && !isLoading && !tab.error && fileType === 'pdf' && (
        <iframe
          key={tab.resource.url}
          src={`${tab.resource.url}#navpanes=0`}
          title={tab.fileName}
          className="h-full w-full border-0"
        />
      )}

      {tab.resource && !isLoading && !tab.error && fileType !== 'image' && fileType !== 'pdf' && (
        <iframe
          key={tab.resource.url}
          src={tab.resource.url}
          title={tab.fileName}
          className="h-full w-full border-0"
          referrerPolicy="no-referrer"
        />
      )}
    </>
  );
}
